import { dynamicSenderLabel } from '@/features/sender-identities';
import type { ChannelId } from '@/services/messaging-channels';
import { isEmptyString } from '@/utils/string';

/**
 * The messaging channels that support opt-outs. The wire values match the
 * backend opt-out condition `type` and the customer opt-out API `channel`
 * field (services core/conditions/optout.go).
 */
export const OPT_OUT_CHANNELS = ['sms', 'whatsapp'] as const satisfies readonly ChannelId[];

export type OptOutChannel = (typeof OPT_OUT_CHANNELS)[number];

// A channel-less opt-out has always meant SMS, both on the wire and in the
// backend (NormalizeOptOutChannel). Keep that default here so stored
// conditions and empty input resolve the same way.
export const DEFAULT_OPT_OUT_CHANNEL: OptOutChannel = 'sms';

const CHANNEL_LABELS = {
  sms: 'SMS',
  whatsapp: 'WhatsApp',
} satisfies Record<OptOutChannel, string>;

/**
 * Coerces a wire value (case-insensitive) to a known channel, falling back to
 * the default (SMS) for empty, legacy, or unknown input. This mirrors the
 * backend, which treats a channel-less opt-out as SMS.
 */
export function normalizeOptOutChannel(value: string | null | undefined): OptOutChannel {
  const lower = (value ?? '').toLowerCase();
  return OPT_OUT_CHANNELS.find((channel) => channel === lower) ?? DEFAULT_OPT_OUT_CHANNEL;
}

export function optOutChannelLabel(channel: OptOutChannel | null | undefined): string {
  return CHANNEL_LABELS[normalizeOptOutChannel(channel)];
}

/**
 * The slice of a workspace sender identity the opt-out helpers read. SMS
 * senders are stored as template_type 'sms' (custom SMS, the default for
 * workspaces without a Twilio integration) or 'twilio' (Twilio-synced);
 * WhatsApp senders are WhatsApp identities.
 */
export type OptOutSenderIdentity = {
  template_type: string;
  phone: string;
  name: string;
  rules?: object | null;
};

export function isDynamicSender(identity: OptOutSenderIdentity): boolean {
  return identity.rules != null;
}

type SenderOption = { label: string; value: string };
type SenderOptionGroup = { label: string; options: SenderOption[] };

/** A sender's label in a condition's sender picker: its name, else its number. */
export function optOutSenderLabel(identity: OptOutSenderIdentity): string {
  if (isDynamicSender(identity)) return dynamicSenderLabel(identity);
  return isEmptyString(identity.name) ? identity.phone : identity.name;
}

function senderOption(identity: OptOutSenderIdentity): SenderOption {
  return { label: optOutSenderLabel(identity), value: identity.phone };
}

// Services accepts both SMS spellings (ui_api/identities/create.go), so both
// must count as the SMS channel here (FPS-3717). The wire is unaffected: the
// serialized condition carries the channel's bitmap type, never the sender's
// template_type.
const CHANNEL_TEMPLATE_TYPES = {
  sms: ['sms', 'twilio'],
  whatsapp: ['whatsapp'],
} satisfies Record<OptOutChannel, readonly string[]>;

export function getOptOutIdentities<TIdentity extends OptOutSenderIdentity>(
  identities: readonly TIdentity[],
  channel: OptOutChannel,
): TIdentity[] {
  const templateTypes = CHANNEL_TEMPLATE_TYPES[channel];
  return identities.filter((identity) => templateTypes.includes(identity.template_type));
}

export function optOutPhoneNumberOptions(
  identities: readonly OptOutSenderIdentity[],
  channel: OptOutChannel,
): (SenderOption | SenderOptionGroup)[] {
  const senders = getOptOutIdentities(identities, channel);
  const anySender: SenderOption = { label: 'Any sender', value: '' };
  const dynamicSenders = senders.filter(isDynamicSender).map(senderOption);
  if (dynamicSenders.length === 0) {
    return [anySender, ...senders.map(senderOption)];
  }
  const numbers = senders.filter((identity) => !isDynamicSender(identity)).map(senderOption);
  const groups: SenderOptionGroup[] = [
    { label: 'Phone numbers', options: numbers },
    { label: 'Dynamic senders', options: dynamicSenders },
  ];
  return [anySender, ...groups.filter((group) => group.options.length > 0)];
}

/**
 * Select options for the row's channel picker: the channels switched on, plus
 * the row's own channel when the workspace has since switched it off, so a
 * stored condition still shows what it targets.
 */
export function optOutChannelOptions(
  enabledActions: readonly string[],
  currentChannel?: OptOutChannel | null,
): { label: string; value: OptOutChannel }[] {
  const options = getEnabledOptOutChannels(enabledActions).map((channel) => ({
    label: CHANNEL_LABELS[channel],
    value: channel,
  }));
  if (currentChannel != null && !options.some((option) => option.value === currentChannel)) {
    return [{ label: CHANNEL_LABELS[currentChannel], value: currentChannel }, ...options];
  }
  return options;
}

/**
 * The integrations that switch each channel on, as the workspace's
 * `enabled_actions` names them. SMS is either custom SMS (`sms`) or a Twilio
 * integration (`twilio`).
 */
const CHANNEL_ENABLED_ACTIONS = {
  sms: ['sms', 'twilio'],
  whatsapp: ['whatsapp'],
} satisfies Record<OptOutChannel, readonly string[]>;

/**
 * The channels the workspace has switched on, in canonical order (SMS before
 * WhatsApp), read from its `enabled_actions`.
 *
 * This answers "does the workspace use this channel" rather than "does it have
 * a sender on it". The two differ: a workspace can have SMS switched on before
 * any sender identity exists, and a surface that gates on this list offers the
 * opt-out condition there.
 */
export function getEnabledOptOutChannels(enabledActions: readonly string[]): OptOutChannel[] {
  return OPT_OUT_CHANNELS.filter((channel) =>
    CHANNEL_ENABLED_ACTIONS[channel].some((action) => enabledActions.includes(action)),
  );
}

export function hasEnabledOptOutChannel(enabledActions: readonly string[]): boolean {
  return getEnabledOptOutChannels(enabledActions).length > 0;
}

/**
 * The channel a new opt-out condition targets on a surface that gates on
 * `enabled_actions`: the first channel switched on, or SMS when none is.
 */
export function defaultEnabledOptOutChannel(enabledActions: readonly string[]): OptOutChannel {
  return getEnabledOptOutChannels(enabledActions)[0] ?? DEFAULT_OPT_OUT_CHANNEL;
}
